import React from 'react'
import PropTypes from 'prop-types'

import { makeStyles, StylesProvider, withStyles } from '@material-ui/core/styles'
import Modal from '@material-ui/core/Modal'
import Backdrop from '@material-ui/core/Backdrop'
import Fade from '@material-ui/core/Fade'
import Close from '@material-ui/icons/Close'
import IconButton from '@material-ui/core/IconButton'
import { LookAndFeel } from 'core/utils/lookAndFeel.js'
import { Box, Flex } from '@rebass/grid'
import { Button } from '@material-ui/core'

const StyledBox = withStyles({
  root: {
    paddingBottom: 0,
    paddingRight: 0,
    paddingTop: 0,
    marginRight: 0,
    marginTop: 0
  }
})(Box);

const StyledIconButton = withStyles({
  root: {
    padding: 6,
    borderRadius: 8,
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: 'rgba(17,24,39,0.06)',
    },
  }
})(IconButton);

const hexToRgb = (hex) => {
  if (!hex) return { r: 0, g: 0, b: 0 }
  const normalized = hex.replace('#', '')
  const val = normalized.length === 3
    ? normalized.split('').map(c => c + c).join('')
    : normalized
  const num = parseInt(val, 16)
  if (isNaN(num)) return { r: 0, g: 0, b: 0 }
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  }
}

const darken = (hex, amount = 0.12) => {
  const { r, g, b } = hexToRgb(hex)
  const clamp = v => Math.max(0, Math.min(255, Math.round(v)))
  return `rgb(${clamp(r * (1 - amount))}, ${clamp(g * (1 - amount))}, ${clamp(b * (1 - amount))})`
}

const useStyles = makeStyles((theme) => {
  const lf = LookAndFeel()
  const primary = lf.buttons?.primary || '#D0006F'
  const primaryHover = lf.buttons?.hover || darken(primary, 0.12)
  const textOnPrimary = lf.buttons?.text || '#FFFFFF'
  const textSecondary = lf.text?.secondary || '#1F2937'
  const textBase = lf.text?.base || '#6B7280'
  const borderSoft = '#E5E7EB'

  return {
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(17, 24, 39, 0.30)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
      padding: theme.spacing(2),
    },
    paper: {
      backgroundColor: theme.palette.background.paper,
      width: '100%',
      maxWidth: 420,
      boxShadow: '0 20px 40px rgba(17, 24, 39, 0.18), 0 8px 16px rgba(17, 24, 39, 0.08)',
      padding: theme.spacing(1.5, 2.5, 3),
      borderRadius: 16,
      border: `1px solid ${borderSoft}`,
      fontFamily: "'Poppins', sans-serif",
      outline: 'none',
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
        padding: theme.spacing(1.5, 2, 2.5),
      },
    },
    closeWrap: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    closeIcon: {
      color: '#6B7280',
      fontSize: 20,
    },
    headerWrap: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      marginTop: theme.spacing(1),
      padding: theme.spacing(0, 1),
    },
    titleWrap: {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(1.5),
    },
    title: {
      fontFamily: "'Poppins', sans-serif",
      fontSize: 24,
      fontWeight: 700,
      letterSpacing: -0.2,
      lineHeight: 1.2,
      color: textSecondary,
      textAlign: 'center',
      margin: 0,
      [theme.breakpoints.down('sm')]: {
        fontSize: 20,
      },
    },
    messageText: {
      fontFamily: "'Poppins', sans-serif",
      fontSize: 15,
      lineHeight: 1.55,
      color: textBase,
      textAlign: 'center',
      padding: theme.spacing(0.5, 1, 2),
      margin: 0,
      [theme.breakpoints.down('sm')]: {
        fontSize: 14,
      },
    },
    actionsRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      marginTop: theme.spacing(2),
      padding: theme.spacing(0, 0.5),
      [theme.breakpoints.down('sm')]: {
        gap: 10,
      },
    },
    cancelButton: {
      flex: 1,
      minHeight: 46,
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 10,
      backgroundColor: '#FFFFFF',
      color: primary,
      border: `1.5px solid ${primary}`,
      textTransform: 'none',
      fontFamily: "'Poppins', sans-serif",
      fontSize: 14.5,
      fontWeight: 600,
      letterSpacing: 0.1,
      boxShadow: 'none',
      transition: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
      '&:hover': {
        backgroundColor: `${primary}10`,
        boxShadow: 'none',
      },
      '&:active': {
        transform: 'translateY(0.5px)',
      },
    },
    okButton: {
      flex: 1,
      minHeight: 46,
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 10,
      backgroundColor: primary,
      color: textOnPrimary,
      border: `1.5px solid ${primary}`,
      textTransform: 'none',
      fontFamily: "'Poppins', sans-serif",
      fontSize: 14.5,
      fontWeight: 600,
      letterSpacing: 0.1,
      boxShadow: '0 2px 4px rgba(17, 24, 39, 0.06), 0 1px 2px rgba(17, 24, 39, 0.04)',
      transition: 'background-color 150ms ease, box-shadow 150ms ease, transform 120ms ease',
      '&:hover': {
        backgroundColor: primaryHover,
        borderColor: primaryHover,
        boxShadow: '0 6px 16px rgba(17, 24, 39, 0.10), 0 2px 6px rgba(17, 24, 39, 0.08)',
      },
      '&:active': {
        transform: 'translateY(1px)',
      },
    },
  }
})

const CustomDialog = ({ isOpen, title, message, okLabel, onOkButton, cancelLabel, onCancelButton, onClose }) => {
  const classes = useStyles()

  const handleOk = () => {
    if (onClose) onClose()
    if (onOkButton) onOkButton()
  }

  const handleCancel = () => {
    if (onClose) onClose()
    if (onCancelButton) onCancelButton()
  }

  return (
    <div>
      <StylesProvider injectFirst>
        <Modal
          disableEnforceFocus
          disableAutoFocus
          className={classes.modal}
          open={isOpen}
          onClose={onClose}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 500 }}>
          <Fade in={isOpen}>
            <div className={classes.paper}>
              <StyledBox className={classes.closeWrap}>
                <StyledBox>
                  <StyledIconButton aria-label='close' onClick={handleCancel} color='inherit'>
                    <Close className={classes.closeIcon} />
                  </StyledIconButton>
                </StyledBox>
              </StyledBox>

              <Flex className={classes.headerWrap}>
                <div className={classes.titleWrap}>
                  <h2 id='title' className={classes.title}>{title || ''}</h2>
                </div>
                {
                  message &&
                  <p className={classes.messageText} id='description'>{message || ''}</p>
                }
              </Flex>

              <Box className={classes.actionsRow}>
                {
                  cancelLabel &&
                  <Button
                    className={classes.cancelButton}
                    variant="outlined"
                    onClick={handleCancel}
                  >
                    {cancelLabel}
                  </Button>
                }
                {
                  okLabel &&
                  <Button
                    className={classes.okButton}
                    variant="contained"
                    color="primary"
                    onClick={handleOk}
                  >
                    {okLabel}
                  </Button>
                }
              </Box>
            </div>
          </Fade>
        </Modal>
      </StylesProvider>
    </div>
  )
}

CustomDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  okLabel: PropTypes.string,
  onOkButton: PropTypes.func,
  cancelLabel: PropTypes.string,
  onCancelButton: PropTypes.func,
  onClose: PropTypes.func,
}

CustomDialog.defaultProps = {
  isOpen: false
}

export default CustomDialog
